/**
 * LegislatorCardGrid Component
 *
 * Uses the shared PersonCard for consistent card layout across the app.
 * Renders legislator-specific chips (chamber, district), committees,
 * sponsored-bills / voting-record stats, and workspace badges.
 */

import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  User,
  Eye,
  Bookmark,
  Building,
  FileText,
  Vote,
} from 'lucide-react';
import PersonCard, { PersonCardChip } from '../PersonCard';
import {
  BaseLegislator,
  TrackedLegislator,
  LegislatorRenderContext,
  LegislatorCardConfig,
} from './types';
import { formatLegislatorNameFromObject } from '../../utils/nameUtils';
import { createBillFilterUrl } from '../../utils/billStatusMapping';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import DistrictMapLink from '../DistrictMapLink';
import { formatPartyDisplay } from '../../utils/partyUtils';
import Button from '@shared/ui/Button';
import { formatDistrict } from '../../utils/districtDisplay';

// =============================================================================
// Types
// =============================================================================

interface LegislatorCardGridProps<T extends BaseLegislator = BaseLegislator> {
  data: T[];
  context: LegislatorRenderContext;
  config?: LegislatorCardConfig;
  gridCols?: string;
  className?: string;
  emptyMessage?: string;
  linkDistrictMap?: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

function getLegislatorId(legislator: BaseLegislator): number {
  return legislator.id || 0;
}

function getLegislatorLink(
  legislatorId: number,
  context: LegislatorRenderContext,
  hash?: string
): string {
  const usePublic = context.usePublicLinks || context.isPublicView;
  const basePath = usePublic ? '/public/legislators' : '/legislators';
  const params = !usePublic && context.workspaceId
    ? `?workspace=${context.workspaceId}`
    : '';
  return `${basePath}/${legislatorId}${params}${hash ? `#${hash}` : ''}`;
}

// =============================================================================
// Default Config
// =============================================================================

const DEFAULT_CARD_CONFIG: LegislatorCardConfig = {
  showContact: true,
  showCommittees: true,
  showSponsoredBills: false,
  showVotingRecords: false,
  showPosition: false,
  showPriority: false,
  actions: [],
};

// =============================================================================
// Component
// =============================================================================

function LegislatorCardGrid<T extends BaseLegislator = BaseLegislator>({
  data,
  context,
  config = DEFAULT_CARD_CONFIG,
  gridCols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  className = '',
  emptyMessage = 'No legislators found matching your filters.',
  linkDistrictMap = true,
}: LegislatorCardGridProps<T>): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = location.pathname + location.search;

  const {
    showContact = true,
    showCommittees = true,
    showSponsoredBills = false,
    showVotingRecords = false,
    showPriority = false,
  } = config;

  if (data.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-text-secondary dark:text-text-dark-secondary">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className={`grid ${gridCols} gap-4 ${className}`}>
      {data.map((legislator) => {
        const legislatorId = getLegislatorId(legislator);
        const tracked = legislator as TrackedLegislator;
        const isTracked = context.trackedLegislatorIds?.has(legislatorId);
        const isPriority = tracked.isPriority;
        const phone = legislator.workPhone || '';
        const billsCount = tracked.sponsoredBillsCount || 0;
        const displayName = formatLegislatorNameFromObject(legislator);

        const chips: PersonCardChip[] = [
          {
            label: legislator.chamber,
            variant: legislator.chamber === 'House' ? 'primary' : 'secondary',
          },
        ];

        const districtInline = linkDistrictMap ? (
          <DistrictMapLink
            chamber={legislator.chamber}
            district={legislator.district}
            className="text-xs text-text-secondary dark:text-text-dark-secondary font-normal"
          >
            {formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)}
          </DistrictMapLink>
        ) : (
          formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)
        );

        // Workspace badges (org-level reports)
        const wsArray = (legislator as any).workspaces as Array<{ name: string; id: string; position: string }> | undefined;

        // Committees section
        const committeesNode = showCommittees && legislator.committees ? (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Building className="w-4 h-4 text-text-tertiary" />
              <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Committees ({legislator.committeeCount || 0})
              </span>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2">
              {legislator.committees}
            </p>
          </div>
        ) : null;

        // Stats row
        const hasStats = showSponsoredBills || showVotingRecords;
        const statsNode = hasStats ? (
          <>
            {showSponsoredBills && (
              <button
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const usePublic = context.usePublicLinks || context.isPublicView;
                  const sessionYear = legislator.sessionYear;
                  const billsUrl = usePublic
                    ? `/public/legislators/${legislatorId}`
                    : createBillFilterUrl(String(legislatorId), undefined, { sessionYear });
                  navigate(billsUrl, { state: { returnUrl: currentUrl } });
                }}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
              >
                <FileText className="w-4 h-4" />
                <span>{billsCount} Bills</span>
              </button>
            )}
            {showVotingRecords && (
              <Link
                to={getLegislatorLink(legislatorId, context, 'voting-records')}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Vote className="w-4 h-4" />
                <span>Voting Record</span>
              </Link>
            )}
          </>
        ) : null;

        // Action-buttons footer (directory mode without stats)
        const showActionButtons = (context.onBookmark || context.onNavigate) && !showSponsoredBills && !showVotingRecords;
        const actionFooter = showActionButtons ? (
          <div className="flex gap-2 border-t border-border-light dark:border-border-dark pt-3 mt-3">
            {context.onBookmark && (
              <Button
                variant="outline"
              className="flex-1 h-10 flex items-center justify-center gap-2"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  context.onBookmark!(legislatorId, legislator.fullName || '');
                }}
              >
                <Bookmark className={`w-4 h-4 ${isTracked ? 'fill-current' : ''}`} />
                <span className="text-sm">{isTracked ? 'Tracked' : 'Track'}</span>
              </Button>
            )}
            <Button
              variant="primary"
              className="flex-1 h-10 flex items-center justify-center gap-2"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (context.onNavigate) context.onNavigate(legislatorId);
                else navigate(getLegislatorLink(legislatorId, context));
              }}
            >
              <Eye className="w-4 h-4" />
              <span className="text-sm">View</span>
            </Button>
          </div>
        ) : null;

        const displayParty = formatPartyDisplay(legislator.partyAffiliation || '') || null;

        return (
          <PersonCard
            key={legislatorId}
            id={legislatorId}
            name={displayName}
            party={displayParty}
            nameHref={getLegislatorLink(legislatorId, context)}
            nameLinkState={{ returnUrl: currentUrl }}
            onNameClick={() => navigate(getLegislatorLink(legislatorId, context), { state: { returnUrl: currentUrl } })}
            photoUrl={legislator.photoUrl}
            fallbackIcon={User}
            chips={chips}
            inlineText={districtInline}
            isPriority={showPriority && isPriority}
            isSelected={context.selectedLegislatorIds?.has(legislatorId)}
            isTracked={isTracked}
            onSelect={context.onSelectLegislator
              ? (checked) => context.onSelectLegislator!(legislatorId, checked)
              : undefined}
            onBookmark={context.onBookmark
              ? () => context.onBookmark!(legislatorId, legislator.fullName || '')
              : undefined}
            bookmarkTitle={isTracked ? 'Manage tracking' : 'Track legislator'}
            showContact={showContact}
            email={legislator.email}
            phone={phone || undefined}
            website={legislator.website}
            onClick={() => {
              if (context.onNavigate) context.onNavigate(legislatorId);
              else navigate(getLegislatorLink(legislatorId, context));
            }}
            badges={wsArray && wsArray.length > 0 ? (
              <WorkspaceBadgeList workspaces={wsArray} className="mb-3" />
            ) : undefined}
            middleContent={committeesNode}
            statsRow={statsNode}
            footerRow={actionFooter}
          />
        );
      })}
    </div>
  );
}

export default LegislatorCardGrid;
